"use client";

import { Plus, Edit, Trash, RefreshCw, CreditCard, AlertCircle, Check } from "lucide-react";
import Pagination from "../components/Pagination.jsx";
import { useEffect, useState } from "react";
import { Deletesubscriptionplane, Getsubscriptionplans } from "../components/Api.jsx";
import UpdateModal from "../components/dashboard/Plane/UpdateModal.jsx";
import CreatePlane from "../components/dashboard/Plane/CreatePlane.jsx";

export default function SubscriptionsPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);

  const [Plane, setPlane] = useState([]);
  const [selectedPlane, setSelectedPlane] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpenForCreate, setIsModalOpenCreate] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedPlanDetails, setSelectedPlanDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    setIsMounted(true);
    fetchData();
  }, []);

  const fetchData = (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    Getsubscriptionplans()
      .then((result) => {
        const payload = result?.data;
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.content)
              ? payload.content
              : [];
        setPlane(list);
        console.log('subscription plans:', list);
      })
      .catch((err) => {
        console.error('Error fetching subscription plans:', err);
        setError('Failed to load subscription plans');
        setPlane([]);
      })
      .finally(() => {
        if (isRefresh) {
          setRefreshing(false);
        } else {
          setLoading(false);
        }
      });
  };

  const handleRefresh = () => {
    fetchData(true);
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const handlePlaneClick = (user) => {
    setSelectedPlane(user);
    setIsModalOpen(true);
  };

  const handleCreateNewPlaneClick = () => {
    setIsModalOpenCreate(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPlane(null);
    setIsModalOpenCreate(false);
    fetchData();
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this subscription plan?')) {
      Deletesubscriptionplane(id)
        .then((result) => {
          console.log('Delete result:', result);
          fetchData();
          showToast('Subscription plan deleted successfully');
        })
        .catch((error) => {
          console.error('Delete error:', error);
          showToast('Failed to delete subscription plan: ' + (error.response?.data?.message || error.message), 'error');
        });
    }
  };

  const openDetailsModal = (plan) => {
    setSelectedPlanDetails(plan);
    setIsDetailsModalOpen(true);
  };

  const closeDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedPlanDetails(null);
  };

  const getPlanDurationLabel = (plan) => {
    if (plan?.timePeriod === 'CustomDays' && plan?.durationDays) {
      return `${plan.durationDays} Days`;
    }
    return plan?.timePeriod || '-';
  };

  if (!isMounted) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-black p-4">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
          }`}>
          <div className="flex items-center gap-2">
            {toast.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <Check className="w-5 h-5" />}
            {toast.message}
          </div>
        </div>
      )}
      {/* Header */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col lg:flex-row justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-start gap-4">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <CreditCard className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-3xl">
                    Create and/or edit subscription plans for users with detailed plan features. For purchasing any other plans (addon plan or session), users will need to purchase any of the plans below.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-all duration-200 disabled:cursor-not-allowed text-sm font-medium shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                aria-label={refreshing ? "Refreshing data" : "Refresh subscription plans"}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium shadow-sm hover:shadow-md transition-all duration-200 flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                onClick={() => handleCreateNewPlaneClick()}
                aria-label="Create new subscription plan"
              >
                <Plus className="w-5 h-5" />
                <span>New Plan</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700 font-medium">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(Array.isArray(Plane) ? Plane : []).slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((plan, index) => (
            <div
              key={plan.id ?? index}
              className="bg-white rounded-xl p-6 hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-blue-300"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">{plan.planName}</h3>
                  <p className="text-3xl font-bold text-blue-600 mt-2">
                    ₹{plan.price}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {getPlanDurationLabel(plan)}
                    </span>
                    {plan.discountPercentage > 0 && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {plan.discountPercentage}% off
                      </span>
                    )}
                  </div>
                  {plan.isActive !== undefined && (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-3 ${plan.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                      }`}>
                      {plan.isActive ? 'Active' : 'Inactive'}
                    </span>
                  )}
                  {plan.autoAssignDietChart && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-3 ml-2 bg-purple-100 text-purple-800">
                      Auto-Assign Diet
                    </span>
                  )}
                </div>
                <div className="flex gap-1">
                  <button
                    className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                    onClick={() => handlePlaneClick(plan)}
                    aria-label={`Edit ${plan.planName} plan`}
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  {/* <button
                    className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                    onClick={() => handleDelete(plan.id)}
                    aria-label={`Delete ${plan.planName} plan`}
                  >
                    <Trash className="w-4 h-4" />
                  </button> */}
                </div>
              </div>
              <button
                className="w-full py-2.5 px-4 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-center"
                onClick={() => openDetailsModal(plan)}
              >
                View Details
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Details Modal */}
      {isDetailsModalOpen && selectedPlanDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" aria-labelledby="plan-details-modal" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col border border-gray-200">
            <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-gray-100 px-6 py-4 rounded-t-2xl">
              <div className="flex justify-between items-start">
                <h2 className="text-2xl font-bold text-gray-900" id="plan-details-modal">{selectedPlanDetails.planName}</h2>
                <button
                  onClick={closeDetailsModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl focus:outline-none focus:ring-2 focus:ring-gray-300 rounded-full p-1"
                  aria-label="Close modal"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="overflow-y-auto px-6 py-5">
              <div className="space-y-6">

                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {getPlanDurationLabel(selectedPlanDetails)}
                  </span>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${selectedPlanDetails.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {selectedPlanDetails.isActive ? 'Active' : 'Inactive'}
                  </span>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${selectedPlanDetails.autoAssignDietChart ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-700'}`}>
                    {selectedPlanDetails.autoAssignDietChart ? 'Auto Diet: Enabled' : 'Auto Diet: Disabled'}
                  </span>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Price</p>
                      <p className="text-3xl font-bold text-blue-600">₹{selectedPlanDetails.price}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600 mb-1">Time Period</p>
                      <p className="font-semibold text-gray-900 text-lg">{getPlanDurationLabel(selectedPlanDetails)}</p>
                    </div>
                  </div>

                  {selectedPlanDetails.discountPercentage > 0 && (
                    <div className="mt-4 pt-4 border-t border-blue-100">
                      <p className="text-sm text-gray-600 mb-1">Discount</p>
                      <p className="font-semibold text-green-600 text-lg">{selectedPlanDetails.discountPercentage}% off</p>
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 text-lg">Description</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {selectedPlanDetails.description || 'No description provided for this plan.'}
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 text-lg">Features</h3>
                  {selectedPlanDetails.features && selectedPlanDetails.features.length > 0 ? (
                    <ul className="space-y-2">
                      {selectedPlanDetails.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-green-500 mr-3 mt-0.5 flex-shrink-0">✓</span>
                          <span className="text-gray-700 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-700 text-sm">No features listed for this plan.</p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-600 uppercase tracking-wide mb-1">Plan ID</p>
                    <p className="font-mono text-sm text-gray-900 break-all">{selectedPlanDetails.id}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-600 uppercase tracking-wide mb-1">Status</p>
                    <p className={`font-semibold text-sm ${selectedPlanDetails.isActive ? 'text-green-600' : 'text-red-600'
                      }`}>
                      {selectedPlanDetails.isActive !== undefined
                        ? (selectedPlanDetails.isActive ? 'Active' : 'Inactive')
                        : 'Unknown'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 z-10 bg-white border-t border-gray-100 px-6 py-3 rounded-b-2xl flex justify-end">
                <button
                  onClick={closeDetailsModal}
                  className="px-5 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium"
                >
                  Close
                </button>
            </div>
          </div>
        </div>
      )}

      {isMounted && isModalOpen && selectedPlane && (
        <UpdateModal user={selectedPlane} onClose={closeModal} onSuccess={() => showToast('Subscription plan updated successfully')} />
      )}

      {isMounted && isModalOpenForCreate && (
        <CreatePlane onClose={closeModal} onSuccess={() => showToast('Subscription plan created successfully')} />
      )}
          {(Array.isArray(Plane) ? Plane : [])?.length > 0 && (

      <Pagination 
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={Math.ceil((Array.isArray((Array.isArray(Plane) ? Plane : [])) ? (Array.isArray(Plane) ? Plane : []).length : ((Array.isArray(Plane) ? Plane : [])?.length || 0)) / itemsPerPage)}
          totalElements={(Array.isArray((Array.isArray(Plane) ? Plane : [])) ? (Array.isArray(Plane) ? Plane : []).length : ((Array.isArray(Plane) ? Plane : [])?.length || 0))}
          pageSize={itemsPerPage}
          setPageSize={setItemsPerPage}
        />
      )}
</div>
  );
}